import { Link } from "react-router";
import { useApi } from "../../../app/ApiProvider";
import { useAuth, useScope } from "../../../app/AuthProvider";
import { useAsync } from "../../../app/useAsync";
import {
	Badge,
	Callout,
	Card,
	LinkButton,
	Metric,
	PageHeader,
	StatusBadge,
	waitReasonLabel,
} from "../../../ui/primitives";
import { EmptyState, ErrorState, LoadingState } from "../../../ui/states";

export function HomePage() {
	const { client } = useApi();
	const { session } = useAuth();
	const scope = useScope();

	const data = useAsync(async () => {
		const [dashboard, approvals, sessions, artifacts] = await Promise.all([
			client.getDashboard(scope),
			client.listApprovals(scope),
			client.listSessions(scope, { limit: 5 }),
			client.listArtifacts(scope, { limit: 5 }),
		]);
		return { dashboard, approvals, sessions, artifacts };
	}, [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow={session?.tenant.name}
				title={`こんにちは、${session?.user.name ?? ""}さん`}
				description="いま止まっている作業と、あなたの判断が必要なものだけを表示しています。"
				actions={<LinkButton to="/app/agents">新しく依頼する</LinkButton>}
			/>

			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}

			{data.status === "ready" && (
				<div className="space-y-6">
					{(() => {
						const pending = data.data.approvals.filter((a) => a.status === "PENDING");
						if (pending.length === 0) return null;
						return (
							<Callout tone="warning" title={`${pending.length}件、あなたの判断を待っています`}>
								<ul className="mt-1.5 space-y-1.5">
									{pending.map((approval) => (
										<li key={approval.id} className="flex flex-wrap items-center gap-2">
											<Link
												to={`/app/approvals/${approval.id}`}
												className="text-[14px] font-semibold text-brand-700 hover:underline"
											>
												{approval.title}
											</Link>
											<span className="text-[12px] text-ink-500">
												{approval.summary.slice(0, 40)}…
											</span>
										</li>
									))}
								</ul>
							</Callout>
						);
					})()}

					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric
							label="進行中の依頼"
							value={data.data.dashboard.summary.runningExecutions}
							unit="件"
						/>
						<Metric label="これまでの依頼" value={data.data.dashboard.summary.sessions} unit="件" />
						<Metric
							label="できあがった成果物"
							value={data.data.dashboard.summary.artifacts}
							unit="件"
						/>
						<Metric
							label="今月の費用"
							value={`¥${Math.round(data.data.dashboard.summary.cost).toLocaleString()}`}
							hint="実費として取得できた分のみ"
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<Card
							title="最近の依頼"
							action={
								<Link
									to="/app/sessions"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									すべて見る
								</Link>
							}
						>
							{data.data.sessions.items.length === 0 ? (
								<EmptyState
									title="まだ依頼がありません"
									description="Agentを選ぶと、質問に答えるだけで作業が始まります。"
									action={
										<LinkButton to="/app/agents" size="sm">
											Agentを探す
										</LinkButton>
									}
								/>
							) : (
								<ul className="divide-y divide-ink-100">
									{data.data.sessions.items.map((item) => (
										<li key={item.id} className="py-2.5 first:pt-0 last:pb-0">
											<Link
												to={`/app/sessions/${item.id}`}
												className="text-[14.5px] font-semibold text-ink-900 hover:text-brand-600"
											>
												{item.title}
											</Link>
											<div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-ink-500">
												<StatusBadge status={item.status} />
												<span>{item.businessPurpose || "目的未設定"}</span>
											</div>
										</li>
									))}
								</ul>
							)}
						</Card>

						<Card
							title="進行中の作業"
							action={
								<Link
									to="/app/progress"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									すべて見る
								</Link>
							}
						>
							{data.data.dashboard.recentExecutions.length === 0 ? (
								<EmptyState title="進行中の作業はありません" />
							) : (
								<ul className="divide-y divide-ink-100">
									{data.data.dashboard.recentExecutions.map((execution) => (
										<li
											key={execution.id}
											className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
										>
											<Link
												to={`/app/progress/${execution.id}`}
												className="min-w-0 text-[14px] text-ink-800 hover:text-brand-600"
											>
												<span className="block truncate font-mono text-[12px] text-ink-400">
													{execution.id}
												</span>
												{waitReasonLabel(execution.waitReason) ?? "作業を進めています"}
											</Link>
											<StatusBadge status={execution.status} />
										</li>
									))}
								</ul>
							)}
						</Card>

						<Card
							title="最近の成果物"
							className="lg:col-span-2"
							action={
								<Link
									to="/app/artifacts"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									すべて見る
								</Link>
							}
						>
							{data.data.artifacts.items.length === 0 ? (
								<EmptyState title="まだ成果物がありません" />
							) : (
								<ul className="grid gap-2 sm:grid-cols-2">
									{data.data.artifacts.items.map((artifact) => (
										<li key={artifact.id}>
											<Link
												to={`/app/artifacts/${artifact.id}`}
												className="block rounded-lg border border-ink-200 px-3 py-2 hover:border-brand-300"
											>
												<span className="block truncate text-[14px] font-semibold text-ink-900">
													{artifact.name}
												</span>
												<span className="mt-0.5 flex items-center gap-2 text-[12px] text-ink-500">
													<Badge tone="neutral">v{artifact.version}</Badge>
													{artifact.mimeType}
												</span>
											</Link>
										</li>
									))}
								</ul>
							)}
						</Card>
					</div>
				</div>
			)}
		</>
	);
}
